import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import axios from "axios";
import { useParams } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ExperienceInformationPopup = ({ experienceData, updateExperienceData }) => {
  const [experiences, setExperiences] = useState([]);
  const [dataFetched, setDataFetched] = useState(false);
  const [selectedFromDates, setSelectedFromDates] = useState([]);
  const [selectedToDates, setSelectedToDates] = useState([]);
  const [errors, setErrors] = useState({});

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const { userId } = useParams();

  useEffect(() => {
    if (!dataFetched && (!experienceData || experienceData.length === 0)) {
      fetchExperienceData();
    }
  }, [experienceData, dataFetched]);

  const fetchExperienceData = async () => {
    try {
      const response = await axios.get(`${apiUrl}/employees/experience-info/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (Array.isArray(response.data.experienceInfo)) {
        setExperiences(response.data.experienceInfo.map(exp => ({ ...exp, isRelevant: false })));
        setSelectedFromDates(response.data.experienceInfo.map(exp => exp.from_date));
        setSelectedToDates(response.data.experienceInfo.map(exp => exp.to_date));
        setExperiences(response.data.experienceInfo);
        updateExperienceData(response.data.experienceInfo);
        setDataFetched(true);
      } else {
        console.error('Fetched education data is not an array:', response.data);
      }
    } catch (error) {
      console.error('Error fetching education data:', error);
    }
  };

  const handleFromDateChange = (date, index) => {
    const newSelectedFromDates = [...selectedFromDates];
    newSelectedFromDates[index] = date;
    setSelectedFromDates(newSelectedFromDates);
  };

  const handleToDateChange = (date, index) => {
    const newSelectedToDates = [...selectedToDates];
    newSelectedToDates[index] = date;
    setSelectedToDates(newSelectedToDates);
  };

  const handleAddMore = () => {
    const newExperience = {
      id: experiences.length + 1,
      employer: "",
      job_title: "",
      from_date: "",
      to_date: "",
      total_work_exp: "0",
      isRelevant: false
    };

    setExperiences([...experiences, newExperience]);
    setSelectedFromDates([...selectedFromDates, ""]);
    setSelectedToDates([...selectedToDates, ""]);
  };

  const handleDelete = (index) => {
    const newExperiences = [...experiences];
    newExperiences.splice(index, 1);
    setExperiences(newExperiences);
    const newSelectedFromDates = [...selectedFromDates];
    newSelectedFromDates.splice(index, 1);
    setSelectedFromDates(newSelectedFromDates);
    const newSelectedToDates = [...selectedToDates];
    newSelectedToDates.splice(index, 1);
    setSelectedToDates(newSelectedToDates);
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    const newExperiences = [...experiences];
    newExperiences[index][name] = value;
    setExperiences(newExperiences);
    validateField(name, value, index);
  };

  const validateField = (name, value, index) => {
    let error = "";

    if (!value) {
      error = "This field is required";
    } else {
      if (name === "total_work_exp" && !/^\d+(\.\d+)?$/.test(value)) {
        error = "Must be a number";
      }
    }

    const newErrors = { ...errors };
    if (error) {
      newErrors[`${name}-${index}`] = error;
    } else {
      delete newErrors[`${name}-${index}`];
    }
    setErrors(newErrors);
  };

  const handleSave = async () => {
    try {
      const formattedExperienceData = experiences.map((experience, index) => ({
        employer: experience.employer,
        job_title: experience.job_title,
        from_date: selectedFromDates[index],
        to_date: selectedToDates[index],
        total_work_exp: experience.total_work_exp || "0",
        isRelevant: experience.isRelevant
      }));

      const requestData = {
        experienceInfo: formattedExperienceData
      };

      const response = await axios.post(
        `${apiUrl}/employees/updateexperienceinfo/${userId}`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.status === 200) {
        console.log('Response from API:', response.data);
        toast.success('Data saved successfully!');
        fetchExperienceData();
      } else {
        console.error('Failed to save data:', response);
        toast.error('Failed to save data. Please try again.');
      }
    } catch (error) {
      console.error('Error while saving data:', error);
      toast.error('Failed to save data. Please try again.');
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    const reorderedData = Array.from(experiences);
    const [movedItem] = reorderedData.splice(result.source.index, 1);
    reorderedData.splice(result.destination.index, 0, movedItem);
    setExperiences(reorderedData);
  };

  return (
    <div id="experience_info" className="modal custom-modal fade" role="dialog">
      <div className="modal-dialog modal-lg" style={{ margin: 0, position: "fixed", right: 0, height: "100vh", width: "100%" }} role="document">
        <div className="modal-content h-100">
          <div className="modal-header">
            <h5 className="modal-title">Experience Information</h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <div className="modal-body" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
            <form>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="experienceList">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      <div className="table-responsive">
                        <table className="table">
                          <thead>
                            <tr>
                              <th>Employer</th>
                              <th>Job Title</th>
                              <th>From Date</th>
                              <th>To Date</th>
                              <th>Experience (Years)</th>
                              <th>Is Relevant</th>
                              <th>
                                <FontAwesomeIcon icon={faPlus} className="add-more-icon" onClick={handleAddMore} />
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {experiences.map((experience, index) => (
                              <Draggable key={index} draggableId={`${index}`} index={index}>
                                {(provided, snapshot) => (
                                  <tr
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className={snapshot.isDragging ? "dragging" : ""}
                                  >
                                    <td>
                                      <input
                                        type="text"
                                        name="employer"
                                        value={experience.employer}
                                        onChange={(e) => handleInputChange(e, index)}
                                        className={`form-control ${errors[`employer-${index}`] ? "is-invalid" : ""}`}
                                      />
                                      {errors[`employer-${index}`] && (
                                        <div className="invalid-feedback">{errors[`employer-${index}`]}</div>
                                      )}
                                    </td>
                                    <td>
                                      <input
                                        type="text"
                                        name="job_title"
                                        value={experience.job_title}
                                        onChange={(e) => handleInputChange(e, index)}
                                        className={`form-control ${errors[`job_title-${index}`] ? "is-invalid" : ""}`}
                                      />
                                      {errors[`job_title-${index}`] && (
                                        <div className="invalid-feedback">{errors[`job_title-${index}`]}</div>
                                      )}
                                    </td>
                                    <td>
                                      <DatePicker
                                        selected={selectedFromDates[index] ? new Date(selectedFromDates[index]) : null}
                                        onChange={(date) => handleFromDateChange(date, index)}
                                        className={`form-control datetimepicker ${errors[`from_date-${index}`] ? "is-invalid" : ""}`}
                                        dateFormat="dd/MM/yyyy"
                                      />
                                      {errors[`from_date-${index}`] && (
                                        <div className="invalid-feedback">{errors[`from_date-${index}`]}</div>
                                      )}
                                    </td>
                                    <td>
                                      <DatePicker
                                        selected={selectedToDates[index] ? new Date(selectedToDates[index]) : null}
                                        onChange={(date) => handleToDateChange(date, index)}
                                        className={`form-control datetimepicker ${errors[`to_date-${index}`] ? "is-invalid" : ""}`}
                                        dateFormat="dd/MM/yyyy"
                                      />
                                      {errors[`to_date-${index}`] && (
                                        <div className="invalid-feedback">{errors[`to_date-${index}`]}</div>
                                      )}
                                    </td>
                                    <td>
                                      <input
                                        type="text"
                                        name="total_work_exp"
                                        value={experience.total_work_exp}
                                        onChange={(e) => handleInputChange(e, index)}
                                        className={`form-control ${errors[`total_work_exp-${index}`] ? "is-invalid" : ""}`}
                                        pattern="^\d+(\.\d+)?$"
                                      />
                                      {errors[`total_work_exp-${index}`] && (
                                        <div className="invalid-feedback">{errors[`total_work_exp-${index}`]}</div>
                                      )}
                                    </td>
                                    <td>
                                      <select
                                        name="isRelevant"
                                        value={experience.isRelevant}
                                        onChange={(e) => handleInputChange(e, index)}
                                        className={`form-select form-select-sm ${errors[`isRelevant-${index}`] ? "is-invalid" : ""}`}
                                        style={{ width: '60px' }}
                                      >
                                        <option value={true}>Yes</option>
                                        <option value={false}>No</option>
                                      </select>
                                      {errors[`isRelevant-${index}`] && (
                                        <div className="invalid-feedback">{errors[`isRelevant-${index}`]}</div>
                                      )}
                                    </td>
                                    <td>
                                      {index === experiences.length - 1 ? (
                                        <FontAwesomeIcon icon={faPlus} className="add-more-icon" onClick={handleAddMore} />
                                      ) : (
                                        <FontAwesomeIcon icon={faTrashAlt} className="delete-icon" onClick={() => handleDelete(index)} />
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </form>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-primary" onClick={handleSave}>Save</button>
            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal" aria-label="Close">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExperienceInformationPopup;
